// /cli?code=ABCD-EFGH: approve (or deny) a `cmx login` from a terminal. Needs a signed-in
// browser; signed-out visitors go to /login and come back here with the code.

import { useEffect, useState } from "react";
import { cli } from "../api";
import { logout } from "../api";
import { navigate, Redirect, useLocation } from "../router";
import { store } from "../store";
import { useSession } from "../session";
import { PageSplash } from "./Shell";
import { HxPage, Icon, NightMark } from "./hx";

type View =
  | { kind: "loading" }
  | { kind: "ready"; code: string; name: string; expiresAt: number }
  | { kind: "invalid"; message: string }
  | { kind: "approved"; name: string }
  | { kind: "denied" };

function useCountdown(until: number | null) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (until === null) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [until]);
  return until === null ? 0 : Math.max(0, Math.round((until - now) / 1000));
}

export function CliApprovePage() {
  const { auth, me } = useSession();
  const { search } = useLocation();
  const code = (search.get("code") ?? "").trim();
  const [view, setView] = useState<View>({ kind: "loading" });
  const [busy, setBusy] = useState<"approve" | "deny" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (auth.kind !== "in") return;
    if (!code) {
      setView({
        kind: "invalid",
        message:
          "This link has no code. Run cmx login in your terminal and open the link it prints.",
      });
      return;
    }
    let alive = true;
    void cli.code(code).then((r) => {
      if (!alive) return;
      if (r.ok)
        setView({
          kind: "ready",
          code: r.data.code,
          name: r.data.name,
          expiresAt: Date.now() + r.data.expiresIn * 1000,
        });
      else
        setView({
          kind: "invalid",
          message:
            r.status === 404
              ? "This code is invalid, expired or already used. Run cmx login again for a new one."
              : r.error,
        });
    });
    return () => {
      alive = false;
    };
  }, [auth.kind, code]);

  const left = useCountdown(view.kind === "ready" ? view.expiresAt : null);
  useEffect(() => {
    if (view.kind === "ready" && left === 0)
      setView({
        kind: "invalid",
        message: "This code expired. Run cmx login again for a new one.",
      });
  }, [left, view.kind]);

  if (auth.kind === "out")
    return (
      <Redirect to={`/login?next=${encodeURIComponent(`/cli?code=${code}`)}`} />
    );
  if (auth.kind === "error")
    return (
      <PageSplash
        title="Can’t reach cmx"
        detail={`${auth.message} — retrying…`}
      />
    );
  if (auth.kind !== "in" || !me) return <PageSplash />;

  const decide = async (approve: boolean) => {
    if (view.kind !== "ready" || busy) return;
    setBusy(approve ? "approve" : "deny");
    setError(null);
    const r = approve
      ? await cli.approve(view.code)
      : await cli.deny(view.code);
    setBusy(null);
    if (r.ok)
      setView(
        approve ? { kind: "approved", name: view.name } : { kind: "denied" },
      );
    else if (r.status === 404)
      setView({
        kind: "invalid",
        message: "This code expired or was already used. Run cmx login again.",
      });
    else setError(r.error);
  };

  // Signed in as someone else: sign out here and come back to this code as the right account.
  const switchAccount = async () => {
    await logout();
    store.set({ me: null, auth: { kind: "out", github: false } });
    navigate(`/login?next=${encodeURIComponent(`/cli?code=${code}`)}`, {
      replace: true,
    });
  };
  // The Cloud Machine app signs in the same way as `cmx login`; say which one is asking.
  const fromApp =
    view.kind === "ready" && /\(Cloud Machine app\)$/.test(view.name);

  const mmss = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`;
  const device = view.kind === "ready" ? view.name.replace(/ \(Cloud Machine app\)$/, "") : "";

  return (
    <HxPage>
      <div className="hx-column" data-testid="cli-approve" data-state={view.kind}>
        {view.kind === "loading" && (
          <div className="hx-column-head" aria-busy="true" aria-label="Loading">
            <NightMark />
          </div>
        )}

        {view.kind === "ready" && (
          <>
            <div className="hx-column-head hx-rise">
              <NightMark />
              <h1 className="hx-h1">
                {fromApp ? "Sign in Cloud Machine on " : "Approve cmx on "}
                <em className="cli-device" data-testid="cli-device">
                  {fromApp ? device : view.name}
                </em>
                ?
              </h1>
              {fromApp ? (
                <p>
                  The Cloud Machine app asked to sign in to your account. It will open your sessions, forward ports and set up your
                  cloud machine. Approve it only if you just pressed Sign in there.
                </p>
              ) : (
                <p>
                  A terminal asked to sign in to your account. It will open your sessions, forward ports and ssh into your cloud
                  machine. Approve it only if you just ran <code className="hx-mono">cmx login</code> there.
                </p>
              )}
            </div>
            <div className="hx-card hx-form hx-rise" style={{ ["--i" as string]: 1 }}>
              <p className="hx-faint hx-cli-expires" aria-live="off">
                Expires in {mmss}
              </p>
              <div className="hx-code">
                {view.code.split("").map((ch, i) =>
                  ch === "-" ? (
                    <span key={i} className="hx-code-dash" aria-hidden="true">
                      –
                    </span>
                  ) : (
                    <span key={i} className="hx-code-ch" aria-hidden="true">
                      {ch}
                    </span>
                  ),
                )}
                <span className="sr-only" data-testid="cli-code">
                  {view.code}
                </span>
              </div>
              <p className="hx-faint" style={{ margin: 0, textAlign: "center", fontSize: 13.5 }}>
                Check that {fromApp ? "the app" : "your terminal"} shows the same code.
              </p>
              {error && (
                <div className="hx-error" role="alert" data-testid="cli-error">
                  {error}
                </div>
              )}
              <button
                className="hx-btn hx-btn-primary hx-btn-block"
                disabled={!!busy}
                onClick={() => void decide(true)}
                data-testid="cli-approve-btn"
                autoFocus
              >
                {busy === "approve" ? "Approving…" : "Approve"}
              </button>
              <button
                className="hx-btn hx-btn-quiet hx-btn-danger hx-btn-block"
                disabled={!!busy}
                onClick={() => void decide(false)}
                data-testid="cli-deny-btn"
              >
                {busy === "deny" ? "Denying…" : "Deny"}
              </button>
            </div>
            <div className="hx-foot hx-cli-meta hx-rise" style={{ ["--i" as string]: 2 }}>
              <span>
                Signed in as <strong>{me.email}</strong> ·{" "}
                <button className="hx-linkbtn hx-linkbtn-accent" onClick={() => void switchAccount()} data-testid="cli-switch-account">
                  Use another account
                </button>
              </span>
            </div>
          </>
        )}

        {view.kind === "approved" && (
          <div className="hx-column-head hx-rise" role="status" data-testid="cli-approved">
            <span className="hx-check hx-check-lg">{Icon.check}</span>
            <h1 className="hx-h1">{/\(Cloud Machine app\)$/.test(view.name) ? <>Back to <em>the app.</em></> : <>Back to <em>your terminal.</em></>}</h1>
            <p>
              <strong>{view.name}</strong> is signed in; you can close this tab. Revoke it any time on the dashboard under Signed-in
              computers.
            </p>
          </div>
        )}

        {view.kind === "denied" && (
          <div className="hx-column-head hx-rise" role="status" data-testid="cli-denied">
            <span className="hx-check hx-check-lg is-bad">✕</span>
            <h1 className="hx-h1">
              Request <em>denied.</em>
            </h1>
            <p>Nothing was signed in. If you didn’t start this, there’s nothing else to do.</p>
          </div>
        )}

        {view.kind === "invalid" && (
          <div className="hx-column-head hx-rise" role="alert" data-testid="cli-invalid">
            <span className="hx-check hx-check-lg is-bad">!</span>
            <h1 className="hx-h1">
              Can’t approve <em>this code.</em>
            </h1>
            <p>{view.message}</p>
            <a className="hx-btn hx-btn-secondary" href="/dashboard">
              Back to dashboard
            </a>
          </div>
        )}
      </div>
    </HxPage>
  );
}
